/**
 * Value Velocity Card
 *
 * Shows quote pipeline movement: total pipeline value, win rate,
 * and the resulting value moved forward (pipeline x win rate).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp } from 'lucide-react';
import { useQuoteFunnel, useQuotesPipelineSummary } from '@/features/operations-financial/hooks/useFinancialMetrics';
import { formatCurrency } from '@/lib/utils';

export function ValueVelocityCard() {
  const { data: funnel, isLoading: funnelLoading } = useQuoteFunnel(1);
  const { data: pipeline, isLoading: pipelineLoading } = useQuotesPipelineSummary();

  const loading = funnelLoading || pipelineLoading;

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardContent>
      </Card>
    );
  }

  const pipelineTotal = pipeline?.total_value ?? 0;
  const winRate = funnel?.win_rate ?? 0;
  const valueMoved = Math.round(pipelineTotal * (winRate / 100));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Value Velocity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-2xl font-bold">{formatCurrency(valueMoved)}</p>
            <p className="text-xs text-muted-foreground">Value moved forward</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold">{formatCurrency(pipelineTotal)}</p>
              <p className="text-xs text-muted-foreground">Pipeline</p>
            </div>
            <div>
              <p className="text-sm font-semibold">{winRate.toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">Win Rate</p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-2">
            {formatCurrency(pipelineTotal)} x {winRate.toFixed(0)}% = {formatCurrency(valueMoved)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
